import React, { useEffect, useMemo, useState } from 'react';
import '../../../../assets/styles/errors.scss';

import {
    adminFetchErrorReports,
    adminGetErrorReport,
    adminUpdateErrorReport,
    adminCreateErrorReport,
    type ErrorReport
} from '../../../../api/errorReports';

const pageSize = 20;

export default function AdminErrors() {
    const [items, setItems] = useState<ErrorReport[]>([]);
    const [pending, setPending] = useState(0);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();

    // filtry
    const [q, setQ] = useState('');
    const [type, setType] = useState<'' | 'TEXT' | 'AUDIO' | 'APP'>('');
    const [resolved, setResolved] = useState<'any' | 'true' | 'false'>('any');

    // szczegóły
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [selected, setSelected] = useState<ErrorReport | null>(null);
    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

    const [newType, setNewType] = useState<'TEXT' | 'AUDIO' | 'APP'>('APP');
    const [newDescription, setNewDescription] = useState('');
    const [newContentId, setNewContentId] = useState<string>('');

    async function createNew() {
        if (!newDescription.trim()) {
            setError('Opis jest wymagany');
            return;
        }
        setLoading(true); setError(undefined);
        try {
            await adminCreateErrorReport({
                type: newType,
                description: newDescription.trim(),
                content_id: newContentId ? Number(newContentId) : undefined,
            });
            setNewDescription('');
            setNewContentId('');
            setNewType('APP');
            await load();
        } catch (e: any) {
            setError(e?.error || e?.message || 'Nie udało się dodać zgłoszenia');
        } finally {
            setLoading(false);
        }
    }

    async function load() {
        setLoading(true); setError(undefined);
        try {
            const res = await adminFetchErrorReports({ q, type, resolved, page, limit: pageSize });
            setItems(res.items);
            setTotal(res.total);
            setPending(res.pending);
        } catch (e: any) {
            setError(e?.message || 'Błąd pobierania zgłoszeń');
        } finally {
            setLoading(false);
        }
    }

    async function openDetails(id: string) {
        setSelectedId(id);
        try {
            const data = await adminGetErrorReport(id);
            setSelected(data);
        } catch (e: any) {
            setError(e?.message || 'Nie udało się pobrać szczegółów');
        }
    }

    async function toggleResolved() {
        if (!selected) return;
        const next = !selected.is_resolved;
        try {
            const updated = await adminUpdateErrorReport(selected.id, { is_resolved: next });
            setSelected(updated);
            // odśwież listę
            await load();
        } catch (e: any) {
            setError(e?.message || 'Nie udało się zaktualizować');
        }
    }

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, type, resolved]);

    return (
        <div className="errors-root">
            {pending > 0 && (
                <div className="banner">
                    Wymagane akcje ({pending})
                </div>
            )}

            <header className="errors-header">
                <h1>Zgłoszenia błędów</h1>
            </header>

            <section className="card errors-inline-add" style={{ marginBottom: 12 }}>
                <div className="form-row">
                    <div className="field errors-inline-add__type">
                        <label htmlFor="newType">Typ</label>
                        <select id="newType" className="select" value={newType} onChange={e => setNewType(e.target.value as any)}>
                            <option value="TEXT">TEXT</option>
                            <option value="AUDIO">AUDIO</option>
                            <option value="APP">APP</option>
                        </select>
                    </div>

                    <div className="field errors-inline-add__desc">
                        <label htmlFor="newDescription">Opis</label>
                        <textarea
                            id="newDescription"
                            className="input"
                            rows={2}
                            value={newDescription}
                            onChange={e => setNewDescription(e.target.value)}
                            placeholder="Krótki opis problemu..."
                        />
                    </div>

                    <div style={{alignItems: 'center'}}>
                        <button className="btn btn-primary errors-inline-add__btn" onClick={createNew} disabled={loading}>
                            {loading ? 'Zapisywanie…' : 'Dodaj zgłoszenie'}
                        </button>
                    </div>
                </div>
            </section>



            <section className="filters card">
                <div className="form-row">
                    <div className="field">
                        <label htmlFor="q">Szukaj</label>
                        <input id="q" className="input" value={q} onChange={e => setQ(e.target.value)} placeholder="Opis, email, profil, tytuł..." />
                    </div>
                    <div className="field">
                        <label htmlFor="type">Typ</label>
                        <select id="type" className="select" value={type} onChange={e => setType(e.target.value as any)}>
                            <option value="">Wszystkie</option>
                            <option value="TEXT">TEXT</option>
                            <option value="AUDIO">AUDIO</option>
                            <option value="APP">APP</option>
                        </select>
                    </div>
                    <div className="field">
                        <label htmlFor="resolved">Status</label>
                        <select id="resolved" className="select" value={resolved} onChange={e => setResolved(e.target.value as any)}>
                            <option value="any">Dowolny</option>
                            <option value="false">Nierozwiązane</option>
                            <option value="true">Rozwiązane</option>
                        </select>
                    </div>
                    <button className="btn btn-primary" onClick={() => { setPage(1); load(); }} disabled={loading}>
                        {loading ? 'Ładowanie…' : 'Filtruj'}
                    </button>
                </div>
            </section>

            <section aria-busy={loading} className="card">
                <div className="table-wrapper">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Typ</th>
                                <th>Treść</th>
                                <th>Zgłaszający</th>
                                <th>Data</th>
                                <th>Status</th>
                                <th>Szczegóły</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(r => (
                                <tr key={r.id} className={r.is_resolved ? '' : 'row-highlight'}>
                                    <td><span className="badge">{r.type}</span></td>
                                    <td>{r.content_title ? `${r.content_title} (${r.content_type || '—'})` : '—'}</td>
                                    <td>{r.user_email || r.profile_name || '—'}</td>
                                    <td>{new Date(r.reported_at).toLocaleString()}</td>
                                    <td>
                                        {r.is_resolved ? <span className="status ok">rozwiązane</span> : <span className="status pending">do akcji</span>}
                                    </td>
                                    <td>
                                        <button className="btn btn-ghost" onClick={() => openDetails(r.id)}>Pokaż</button>
                                    </td>
                                </tr>
                            ))}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={6} className="empty">Brak zgłoszeń</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="pagination">
                    <button className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>◀</button>
                    <span>Strona {page} / {Math.max(1, Math.ceil(total / pageSize))}</span>
                    <button className="btn btn-ghost" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)}>▶</button>
                </div>
            </section>

            {/* Panel szczegółów */}
            {selectedId && selected && (
                <div className="sidepanel-backdrop" onClick={() => { setSelectedId(null); setSelected(null); }}>
                    <aside className="sidepanel" onClick={e => e.stopPropagation()}>
                        <div className="sidepanel-header">
                            <h3>Zgłoszenie #{selected.id}</h3>
                            <button className="btn btn-ghost" onClick={() => { setSelectedId(null); setSelected(null); }}>✕</button>
                        </div>
                        <div className="sidepanel-body">
                            <div className="kv">
                                <span>Typ</span><strong>{selected.type}</strong>
                            </div>
                            <div className="kv">
                                <span>Treść</span><strong>{selected.content_title || '—'}</strong>
                            </div>
                            <div className="kv">
                                <span>Zgłaszający</span><strong>{selected.user_email || selected.profile_name || '—'}</strong>
                            </div>
                            <div className="kv">
                                <span>Data</span><strong>{new Date(selected.reported_at).toLocaleString()}</strong>
                            </div>
                            <div className="desc">
                                <label>Opis problemu</label>
                                <div className="desc-box">{selected.description || '—'}</div>
                            </div>
                        </div>
                        <div className="sidepanel-footer">
                            <button className="btn btn-ghost" onClick={toggleResolved}>
                                {selected.is_resolved ? 'Oznacz jako nierozwiązane' : 'Oznacz jako rozwiązane'}
                            </button>
                            {!selected.is_resolved && <span className="pill">WYMAGANA AKCJA</span>}
                        </div>
                    </aside>
                </div>
            )}

            {error && <div className="alert error">{error}</div>}
        </div>
    );
}
